import { useState } from 'react';
import { Stethoscope, Play, Upload, Clock, CheckCircle, XCircle, TrendingDown, TrendingUp, Minus, FileText, Zap, AlertTriangle, History, Database, Plus, GitCompare, X, ChevronLeft, Eye, Trash2 } from 'lucide-react';

// ── 测试集（Dataset）：一次维护、多次复用；单模板=纯问题列 ──
// 本平台智能体均为开放/非确定的实时诊断类，无确定可判定的标准答案，
// 故不做答案断言，只评可观测指标（跑通率/时延/错误码）；答案质量交项目组 judge 评。
const DATASETS_INIT = [
  { id: 'ds1', name: '告警诊断冒烟集',
    cases: [
      { q: '查询 NE-HB-Core-01 当前告警' },
      { q: '分析 LINK_DOWN 告警根因' },
      { q: '抽取告警涉及的网元ID' },
      { q: 'CPU 95% 飙高原因分析' },
      { q: '给出该告警处置建议' },
      { q: '触发越权配置查询' },
    ] },
  { id: 'ds2', name: 'KPI 分析集',
    cases: [
      { q: '分析端口流量突降原因' },
      { q: '给出 CPU 飙高处置建议' },
      { q: '近1小时丢包率趋势分析' },
      { q: '该设备是否需要扩容、为什么' },
    ] },
];

// 给某测试集生成一次运行结果（mock：不同 seed 略有差异，模拟两次运行间的变化）
function runDataset(ds, note, seed = 0) {
  const cases = ds.cases.map((c, i) => {
    const ok = !(/越权/.test(c.q)) && !(seed === 2 && /处置建议/.test(c.q)); // 模拟某版本退化
    const latency = +(0.7 + (i % 3) * 0.7 + (seed === 2 && /根因|处置/.test(c.q) ? 2.5 : 0) + seed * 0.05).toFixed(1);
    const code = ok ? 200 : (/越权/.test(c.q) ? 403 : 504);
    return { q: c.q, ok, latency, code };
  });
  const success = cases.filter(c => c.ok).length;
  const lat = cases.map(c => c.latency).sort((a, b) => a - b);
  return {
    id: 'run-' + Math.random().toString(36).slice(2, 7), type: 'batch',
    time: new Date().toLocaleString('zh-CN', { hour12: false }).slice(5),
    datasetId: ds.id, datasetName: ds.name, note: note || '',
    total: cases.length, success, avgLatency: +(lat.reduce((a, b) => a + b, 0) / lat.length).toFixed(1),
    p95: lat[Math.floor(lat.length * 0.95)] || lat[lat.length - 1], cases,
  };
}

// 预置两条历史 run（同一测试集的两次运行）→ 回归可直接选两条对比
// 注：版本=运行批次本身（时间/运行号）；note 为测试者可选备注，平台不自动识别外部 Agent 内部配置
const PRESET_RUNS = [
  { ...runDataset(DATASETS_INIT[0], '', 0), id: 'run-base', seq: 12, time: '06-09 15:20', note: '（基线）' },
  { ...runDataset(DATASETS_INIT[0], '', 2), id: 'run-cand', seq: 13, time: '06-10 10:05', note: '改了告警分类 prompt' },
];
const PRESET_SINGLE = [
  { id: 's1', type: 'single', time: '06-10 09:50', q: '查询 NE-HB-Core-01 的当前告警', code: 200, totalLatency: 2.34, firstByteLatency: 0.62 },
];

function Tab({ active, onClick, icon: Icon, children }) {
  return (
    <button onClick={onClick} className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${active ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}>
      <Icon className="w-4 h-4" /> {children}
    </button>
  );
}
function StatBox({ label, value, sub, color = 'gray' }) {
  const c = { gray: 'text-gray-900', green: 'text-green-600', red: 'text-red-500', orange: 'text-orange-500' }[color];
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-4">
      <div className="text-xs text-gray-500 mb-1">{label}</div>
      <div className={`text-2xl font-bold ${c}`}>{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

export default function DiagnosticTool() {
  const [tab, setTab] = useState('batch');
  const [agent, setAgent] = useState('告警智能诊断');
  const [datasets, setDatasets] = useState(DATASETS_INIT);
  const [runs, setRuns] = useState(PRESET_RUNS);      // 批量运行历史
  const [singleRuns, setSingleRuns] = useState(PRESET_SINGLE); // 单次调试历史
  const [toast, setToast] = useState('');

  const [historyOpen, setHistoryOpen] = useState(false); // 右侧运行历史抽屉
  const [seq, setSeq] = useState(14);                     // 运行号自增
  // 批量测试
  const [dsId, setDsId] = useState('ds1');
  const [note, setNote] = useState('');                  // 本次运行备注（可选，人工备忘）
  const [lastRun, setLastRun] = useState(null);
  // 单次调试
  const [singleInput, setSingleInput] = useState('查询 NE-HB-Core-01 的当前告警');
  const [singleRes, setSingleRes] = useState(null);
  // 回归
  const [baseId, setBaseId] = useState('run-base');
  const [candId, setCandId] = useState('run-cand');
  const [regRan, setRegRan] = useState(false);

  const showToast = (m) => { setToast(m); setTimeout(() => setToast(''), 2400); };
  const ds = datasets.find(d => d.id === dsId);
  const batchRuns = runs.filter(r => r.type === 'batch');

  const runBatch = () => {
    const r = { ...runDataset(ds, note, 1), seq };
    setSeq(s => s + 1);
    setLastRun(r); setRuns(prev => [r, ...prev]);
    showToast(`运行 #${seq} 已存入运行历史`);
  };
  const runSingle = () => {
    const res = { id: 's' + Date.now(), type: 'single', time: new Date().toLocaleString('zh-CN', { hour12: false }).slice(5), q: singleInput, code: 200, totalLatency: +(1.5 + Math.random() * 1.5).toFixed(2), firstByteLatency: +(0.4 + Math.random() * 0.4).toFixed(2) };
    setSingleRes(res); setSingleRuns(prev => [res, ...prev]);
  };
  const addDataset = () => {
    const id = 'ds' + (datasets.length + 1);
    setDatasets(prev => [...prev, { id, name: `上传集 ${id}`, cases: DATASETS_INIT[1].cases }]);
    setDsId(id); showToast('测试集已上传（demo 用示例数据填充）');
  };
  const viewRun = (r) => { setLastRun(r); setTab('batch'); setHistoryOpen(false); };
  const delRun = (id) => { setRuns(prev => prev.filter(r => r.id !== id)); setLastRun(lr => lr && lr.id === id ? null : lr); showToast('已删除该运行记录'); };
  const delSingle = (id) => { setSingleRuns(prev => prev.filter(r => r.id !== id)); showToast('已删除该调试记录'); };

  return (
    <div className="max-w-5xl mx-auto px-6 py-6">
      {toast && <div className="fixed top-4 right-4 z-50 bg-gray-900 text-white text-xs px-4 py-2.5 rounded-lg shadow-lg">{toast}</div>}
      <div className="mb-5">
        <div className="flex items-center gap-2 mb-1">
          <Stethoscope className="w-5 h-5 text-teal-600" />
          <h2 className="text-xl font-bold text-gray-900">Agent 诊断工具</h2>
          <span className="text-xs px-1.5 py-0.5 bg-teal-100 text-teal-700 rounded-full font-medium">三期 P1</span>
        </div>
        <p className="text-sm text-gray-500">上线前/迭代时的<strong>质量评测</strong>（区别于配置时的"接口测试"）：测试集 → 运行（留历史）→ 选两次运行做回归对比。仅评测平台侧可观测指标。</p>
      </div>

      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
          <Tab active={tab === 'batch'} onClick={() => setTab('batch')} icon={FileText}>批量测试</Tab>
          <Tab active={tab === 'single'} onClick={() => setTab('single')} icon={Zap}>单次调试</Tab>
          <Tab active={tab === 'regression'} onClick={() => setTab('regression')} icon={GitCompare}>回归对比</Tab>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-400">诊断对象</span>
          <select value={agent} onChange={e => setAgent(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg">
            <option>告警智能诊断</option><option>KPI异常分析</option><option>工单处理助手</option>
          </select>
        </div>
      </div>

      {/* ── 批量测试 ── */}
      {tab === 'batch' && (
        <div>
          <div className="flex items-center gap-3 mb-3 p-4 bg-white border border-gray-200 rounded-xl flex-wrap">
            <span className="text-sm text-gray-600 flex items-center gap-1"><Database className="w-4 h-4 text-teal-500" /> 测试集</span>
            <select value={dsId} onChange={e => setDsId(e.target.value)} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg">
              {datasets.map(d => <option key={d.id} value={d.id}>{d.name}（{d.cases.length}条）</option>)}
            </select>
            <button onClick={addDataset} className="flex items-center gap-1 px-2.5 py-1.5 text-xs text-teal-600 border border-teal-200 rounded-lg hover:bg-teal-50"><Upload className="w-3.5 h-3.5" /> 上传测试集（CSV）</button>
            <span className="text-sm text-gray-600 ml-2">本次运行备注</span>
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="可选，如：改了告警分类 prompt" className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg w-56" />
            <button onClick={runBatch} className="ml-auto flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700"><Play className="w-4 h-4" /> 运行批量测试</button>
          </div>
          <p className="text-xs text-gray-400 mb-5 flex items-center gap-1"><FileText className="w-3 h-3" /> 测试集为<strong>纯问题列</strong>。每次运行按<strong>批次（运行号+时间）</strong>存入运行历史；"备注"是测试者可选填的人工备忘。仅评<strong>可观测指标</strong>（跑通率/时延/错误码）——本平台智能体均为开放/非确定回答，无标准答案可断言，答案质量交项目组在 Agent 构建侧用 LLM-judge 评。</p>

          {!lastRun ? (
            <div className="text-center py-16 text-gray-400"><FileText className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">选择测试集与配置版本，运行后生成报告并存入历史</p></div>
          ) : <BatchResult r={lastRun} />}
        </div>
      )}

      {/* ── 单次调试 ── */}
      {tab === 'single' && (
        <div>
          <div className="flex gap-2 mb-3">
            <input value={singleInput} onChange={e => setSingleInput(e.target.value)} className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
            <button onClick={runSingle} className="flex items-center gap-1.5 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700"><Play className="w-4 h-4" /> 调试</button>
          </div>
          {singleRes ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <StatBox label="HTTP 状态码" value={singleRes.code} color="green" />
                <StatBox label="总时延" value={`${singleRes.totalLatency}s`} color="orange" />
                <StatBox label="首包时延" value={`${singleRes.firstByteLatency}s`} sub="TTFB" color="orange" />
              </div>
              <div className="bg-white border border-gray-200 rounded-xl p-4">
                <div className="text-xs font-medium text-gray-600 mb-2">响应内容</div>
                <pre className="text-xs font-mono text-green-400 bg-gray-900 rounded p-3 overflow-x-auto">{'{ "answer": "...", "references": [...] }'}</pre>
              </div>
              <p className="text-xs text-gray-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> 不展示 Token（外部 API 型 Agent 内部不可见）；本次调试已记入「运行历史」</p>
            </div>
          ) : (
            <div className="text-center py-16 text-gray-400"><Zap className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">发送单条问题，拆解时延与响应；结果记入运行历史</p></div>
          )}
        </div>
      )}

      {/* ── 回归对比：选两条历史 run（基线 vs 候选）── */}
      {tab === 'regression' && (() => {
        const base = batchRuns.find(r => r.id === baseId);
        const cand = batchRuns.find(r => r.id === candId);
        const sameDs = base && cand && base.datasetId === cand.datasetId;
        return (
        <div>
          <div className="mb-4 p-3 rounded-xl border border-blue-100 bg-blue-50 text-xs text-blue-700 leading-relaxed">
            回归对比 = 从运行历史里选<strong>两条批量运行</strong>（基线=变更前、候选=变更后）做对比。这里的"变更前/后"指<strong>两个运行批次</strong>（运行号+时间）——平台不假定知道你改了什么内部配置（外部 Agent 不可观测），改了什么由你跑前填的<strong>备注</strong>说明。仅比可观测指标（跑通率/时延/错误码），不判答案对错；对所有 Agent 适用。两条运行须基于<strong>同一测试集</strong>才可比。
          </div>
          <div className="flex items-center gap-3 mb-5 p-4 bg-white border border-gray-200 rounded-xl flex-wrap">
            <span className="text-sm text-gray-600">基线（变更前）</span>
            <select value={baseId} onChange={e => { setBaseId(e.target.value); setRegRan(false); }} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg">
              {batchRuns.map(r => <option key={r.id} value={r.id}>#{r.seq} · {r.time}{r.note ? ' · ' + r.note : ''}</option>)}
            </select>
            <TrendingUp className="w-4 h-4 text-gray-300" />
            <span className="text-sm text-gray-600">候选（变更后）</span>
            <select value={candId} onChange={e => { setCandId(e.target.value); setRegRan(false); }} className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg">
              {batchRuns.map(r => <option key={r.id} value={r.id}>#{r.seq} · {r.time}{r.note ? ' · ' + r.note : ''}</option>)}
            </select>
            <button onClick={() => setRegRan(true)} disabled={!sameDs || baseId === candId} className={`ml-auto flex items-center gap-1.5 px-4 py-2 text-sm rounded-lg ${sameDs && baseId !== candId ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-gray-100 text-gray-400'}`}><GitCompare className="w-4 h-4" /> 运行对比</button>
          </div>
          {!sameDs && <p className="text-xs text-red-500 mb-4 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> 两条运行的测试集不一致，不可对比；请选同一测试集的两次运行。</p>}
          {baseId === candId && <p className="text-xs text-amber-500 mb-4">基线与候选不能是同一条运行。</p>}

          {regRan && sameDs && base && cand && <RegressionResult base={base} cand={cand} />}
          {!regRan && <div className="text-center py-12 text-gray-400"><GitCompare className="w-8 h-8 mx-auto mb-2 opacity-40" /><p className="text-sm">选好基线与候选运行，点"运行对比"</p></div>}
        </div>
        );
      })()}

      {/* ── 右侧"运行历史"竖条按钮 ── */}
      <button onClick={() => setHistoryOpen(true)} className="fixed right-0 top-1/3 z-30 flex items-center gap-1 px-2 py-3 bg-teal-600 text-white rounded-l-lg shadow-md hover:bg-teal-700" style={{ writingMode: 'vertical-rl' }}>
        <History className="w-4 h-4" style={{ writingMode: 'horizontal-tb' }} /> 运行历史
      </button>

      {/* ── 右侧抽屉：运行历史 ── */}
      {historyOpen && (
        <div className="fixed inset-0 z-40 flex justify-end">
          <div className="absolute inset-0 bg-black/30" onClick={() => setHistoryOpen(false)} />
          <div className="relative w-[560px] max-w-[92vw] bg-gray-50 h-full shadow-xl flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 bg-white">
              <h3 className="text-base font-semibold text-gray-900 flex items-center gap-1.5"><History className="w-4 h-4 text-teal-600" /> 运行历史</h3>
              <button onClick={() => setHistoryOpen(false)} className="p-1 hover:bg-gray-100 rounded text-gray-400"><X className="w-5 h-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 border-b border-gray-100 text-sm font-semibold text-gray-800 flex items-center gap-1.5"><FileText className="w-4 h-4 text-gray-500" /> 批量运行</div>
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50 text-gray-500">
                    <th className="text-left px-3 py-2 font-medium">运行</th><th className="text-left px-2 py-2 font-medium">备注</th>
                    <th className="px-2 py-2 font-medium">跑通</th><th className="px-2 py-2 font-medium">平均时延</th><th className="px-2 py-2 font-medium">P95</th><th className="px-3 py-2 font-medium">回归</th>
                  </tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {batchRuns.map(r => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-600">#{r.seq}<div className="text-gray-300">{r.time}</div></td>
                        <td className="px-2 py-2 text-gray-600">{r.note || <span className="text-gray-300">—</span>}<div className="text-gray-300">{r.datasetName}</div></td>
                        <td className="px-2 py-2 text-center text-green-600">{Math.round(r.success / r.total * 100)}%</td>
                        <td className="px-2 py-2 text-center text-gray-600">{r.avgLatency}s</td>
                        <td className="px-2 py-2 text-center text-orange-500">{r.p95}s</td>
                        <td className="px-3 py-2 whitespace-nowrap">
                          <button onClick={() => viewRun(r)} title="回看明细" className="text-gray-400 hover:text-teal-600 mr-1"><Eye className="w-3.5 h-3.5 inline" /></button>
                          <button onClick={() => { setBaseId(r.id); setTab('regression'); setRegRan(false); setHistoryOpen(false); }} className="text-teal-600 hover:text-teal-700 mr-1">设基线</button>
                          <button onClick={() => { setCandId(r.id); setTab('regression'); setRegRan(false); setHistoryOpen(false); }} className="text-teal-600 hover:text-teal-700 mr-1">设候选</button>
                          <button onClick={() => delRun(r.id)} title="删除" className="text-gray-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5 inline" /></button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                <div className="px-4 py-2.5 border-b border-gray-100 text-sm font-semibold text-gray-800 flex items-center gap-1.5"><Zap className="w-4 h-4 text-gray-500" /> 单次调试</div>
                <table className="w-full text-xs">
                  <thead><tr className="bg-gray-50 text-gray-500"><th className="text-left px-3 py-2 font-medium">时间</th><th className="text-left px-2 py-2 font-medium">问题</th><th className="px-2 py-2 font-medium">码</th><th className="px-2 py-2 font-medium">时延</th><th className="px-3 py-2 font-medium"></th></tr></thead>
                  <tbody className="divide-y divide-gray-50">
                    {singleRuns.map(r => (
                      <tr key={r.id} className="hover:bg-gray-50"><td className="px-3 py-2 text-gray-600">{r.time}</td><td className="px-2 py-2 text-gray-700">{r.q}</td><td className="px-2 py-2 text-center text-green-600">{r.code}</td><td className="px-2 py-2 text-center text-gray-600">{r.totalLatency}s</td><td className="px-3 py-2 text-center"><button onClick={() => delSingle(r.id)} title="删除" className="text-gray-300 hover:text-red-500"><Trash2 className="w-3.5 h-3.5 inline" /></button></td></tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-400 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> 每次批量/单次运行自动留存；批量运行可一键设为回归对比的基线/候选。</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// 批量结果展示
function BatchResult({ r }) {
  return (
    <>
      <div className="text-xs text-gray-400 mb-3">运行 #{r.seq} · {r.datasetName} · {r.time}{r.note ? ' · ' : ''}<span className="text-gray-600">{r.note}</span>（已存入运行历史）</div>
      <div className="grid grid-cols-4 gap-4 mb-4">
        <StatBox label="总请求" value={r.total} />
        <StatBox label="跑通率" value={`${Math.round(r.success / r.total * 100)}%`} sub={`${r.success}/${r.total}`} color="green" />
        <StatBox label="平均时延" value={`${r.avgLatency}s`} color="orange" />
        <StatBox label="P95 时延" value={`${r.p95}s`} color="orange" />
      </div>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 text-sm font-semibold text-gray-800">用例明细</div>
        <table className="w-full text-sm">
          <thead><tr className="bg-gray-50 text-xs text-gray-500">
            <th className="text-left px-5 py-2 font-medium">测试问题</th>
            <th className="px-4 py-2 font-medium w-16">跑通</th>
            <th className="text-right px-4 py-2 font-medium w-20">时延</th>
            <th className="text-right px-5 py-2 font-medium w-28">状态码</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-50">
            {r.cases.map((c, i) => (
              <tr key={i} className="hover:bg-gray-50">
                <td className="px-5 py-2.5 text-gray-700">{c.q}</td>
                <td className="px-4 py-2.5 text-center">{c.ok ? <CheckCircle className="w-4 h-4 text-green-500 inline" /> : <XCircle className="w-4 h-4 text-red-400 inline" />}</td>
                <td className="px-4 py-2.5 text-right text-gray-600">{c.latency}s</td>
                <td className="px-5 py-2.5 text-right">{c.ok ? <span className="text-green-600">{c.code}</span> : <span className="text-red-500">{c.code}</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-400 mt-3 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> 仅评平台侧可观测指标（跑通率/时延/错误码）。开放回答的深度质量评分（LLM-judge/语义相似）由项目组在 Agent 构建侧完成，不在本工具。</p>
    </>
  );
}

// 回归对比结果：逐题匹配两条 run
function RegressionResult({ base, cand }) {
  const map = Object.fromEntries(base.cases.map(c => [c.q, c]));
  const rows = cand.cases.map(c => {
    const b = map[c.q]; if (!b) return null;
    let verdict = 'stable';
    if (b.ok && !c.ok) verdict = 'degraded';
    else if (!b.ok && c.ok) verdict = 'improved';
    else if (c.latency > b.latency * 1.5) verdict = 'degraded';
    else if (c.latency < b.latency * 0.7) verdict = 'improved';
    return { q: c.q, before: b, after: c, verdict };
  }).filter(Boolean);
  const degraded = rows.filter(r => r.verdict === 'degraded').length;
  return (
    <div>
      <div className="flex items-center gap-2 mb-3 text-xs text-gray-500">
        <span className="px-2 py-1 bg-gray-100 rounded">基线：运行 #{base.seq} <span className="text-gray-400">({base.time}{base.note ? ' · ' + base.note : ''})</span></span>
        <TrendingUp className="w-3.5 h-3.5 text-gray-300" />
        <span className="px-2 py-1 bg-indigo-50 text-indigo-600 rounded">候选：运行 #{cand.seq} <span className="text-indigo-300">({cand.time}{cand.note ? ' · ' + cand.note : ''})</span></span>
        <span className="ml-2">测试集：{base.datasetName}</span>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="bg-gray-50 text-xs text-gray-500">
            <th className="text-left px-5 py-2 font-medium">测试问题</th>
            <th className="px-4 py-2 font-medium">基线（跑通/时延）</th>
            <th className="px-4 py-2 font-medium">候选（跑通/时延）</th>
            <th className="px-5 py-2 font-medium w-24">判定</th>
          </tr></thead>
          <tbody className="divide-y divide-gray-50">
            {rows.map((r, i) => (
              <tr key={i} className={`hover:bg-gray-50 ${r.verdict === 'degraded' ? 'bg-red-50/40' : ''}`}>
                <td className="px-5 py-2.5 text-gray-700">{r.q}</td>
                <td className="px-4 py-2.5 text-center text-xs text-gray-500">{r.before.ok ? '✓' : '✗'} {r.before.latency}s</td>
                <td className="px-4 py-2.5 text-center text-xs text-gray-500">{r.after.ok ? '✓' : '✗'} {r.after.latency}s</td>
                <td className="px-5 py-2.5">
                  {r.verdict === 'degraded' && <span className="flex items-center gap-1 text-red-500 text-xs font-medium"><TrendingDown className="w-3.5 h-3.5" /> 劣化</span>}
                  {r.verdict === 'improved' && <span className="flex items-center gap-1 text-green-600 text-xs font-medium"><TrendingUp className="w-3.5 h-3.5" /> 改善</span>}
                  {r.verdict === 'stable' && <span className="flex items-center gap-1 text-gray-400 text-xs"><Minus className="w-3.5 h-3.5" /> 持平</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-5 py-3 border-t border-gray-100 bg-red-50 text-xs text-red-600">
          <AlertTriangle className="w-3 h-3 inline mr-1" /> 候选相对基线检出 {degraded} 项指标劣化（非答案对错），建议排查后再发布
        </div>
      </div>
    </div>
  );
}
